import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "hd2:ownedItems";
const CHANGE_EVENT = "hd2:ownedItemsChange";
const RESET_SENTINEL_KEY = "hd2:ownedItems:reset:armor-v1";

function applyOneTimeReset(): void {
	if (typeof localStorage === "undefined") {
		return;
	}
	if (localStorage.getItem(RESET_SENTINEL_KEY) === "1") {
		return;
	}
	localStorage.removeItem(STORAGE_KEY);
	localStorage.setItem(RESET_SENTINEL_KEY, "1");
}

interface StoredOwnedItemsV1 {
	version: 1;
	items: string[];
}

interface StoredOwnedItemsV2 {
	version: 2;
	owned: string[];
	excluded: string[];
}

type StoredOwnedItems = StoredOwnedItemsV1 | StoredOwnedItemsV2;

interface OwnedState {
	owned: Set<string>;
	excluded: Set<string>;
}

function emptyState(): OwnedState {
	return { owned: new Set(), excluded: new Set() };
}

function read(): OwnedState {
	applyOneTimeReset();
	const raw = localStorage.getItem(STORAGE_KEY);
	if (!raw) {
		return emptyState();
	}
	try {
		const parsed = JSON.parse(raw) as StoredOwnedItems;
		if (parsed.version === 1) {
			return { owned: new Set(parsed.items), excluded: new Set() };
		}
		if (parsed.version === 2) {
			return {
				owned: new Set(parsed.owned),
				excluded: new Set(parsed.excluded),
			};
		}
		return emptyState();
	} catch {
		return emptyState();
	}
}

function write(state: OwnedState) {
	const data: StoredOwnedItemsV2 = {
		version: 2,
		owned: [...state.owned],
		excluded: [...state.excluded],
	};
	localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	window.dispatchEvent(new Event(CHANGE_EVENT));
}

export interface UseOwnedItemsResult {
	owned: Set<string>;
	excluded: Set<string>;
	has(slug: string): boolean;
	isExcluded(slug: string): boolean;
	toggle(slug: string): void;
	set(slug: string, owned: boolean): void;
	setMany(slugs: string[], owned: boolean): void;
	setExcluded(slug: string, excluded: boolean): void;
	setExcludedMany(slugs: string[], excluded: boolean): void;
	setEffectiveOwnership(
		slug: string,
		sourceOwned: boolean,
		nextOwned: boolean,
	): void;
}

export function useOwnedItems(): UseOwnedItemsResult {
	const [state, setState] = useState<OwnedState>(() => read());

	useEffect(() => {
		const sync = () => {
			setState(read());
		};
		window.addEventListener("storage", sync);
		window.addEventListener(CHANGE_EVENT, sync);
		return () => {
			window.removeEventListener("storage", sync);
			window.removeEventListener(CHANGE_EVENT, sync);
		};
	}, []);

	const has = useCallback((slug: string) => state.owned.has(slug), [state]);
	const isExcluded = useCallback(
		(slug: string) => state.excluded.has(slug),
		[state],
	);

	const toggle = useCallback((slug: string) => {
		const current = read();
		if (current.owned.has(slug)) {
			current.owned.delete(slug);
		} else {
			current.owned.add(slug);
		}
		write(current);
	}, []);

	const set = useCallback((slug: string, isOwned: boolean) => {
		const current = read();
		if (isOwned) {
			current.owned.add(slug);
		} else {
			current.owned.delete(slug);
		}
		write(current);
	}, []);

	const setMany = useCallback((slugs: string[], isOwned: boolean) => {
		const current = read();
		for (const slug of slugs) {
			if (isOwned) {
				current.owned.add(slug);
			} else {
				current.owned.delete(slug);
			}
		}
		write(current);
	}, []);

	const setExcluded = useCallback((slug: string, isExcl: boolean) => {
		const current = read();
		if (isExcl) {
			current.excluded.add(slug);
		} else {
			current.excluded.delete(slug);
		}
		write(current);
	}, []);

	const setExcludedMany = useCallback((slugs: string[], isExcl: boolean) => {
		const current = read();
		for (const slug of slugs) {
			if (isExcl) {
				current.excluded.add(slug);
			} else {
				current.excluded.delete(slug);
			}
		}
		write(current);
	}, []);

	const setEffectiveOwnership = useCallback(
		(slug: string, sourceOwned: boolean, nextOwned: boolean) => {
			const current = read();
			if (sourceOwned) {
				if (nextOwned) {
					current.excluded.delete(slug);
				} else {
					current.excluded.add(slug);
					current.owned.delete(slug);
				}
			} else {
				current.excluded.delete(slug);
				if (nextOwned) {
					current.owned.add(slug);
				} else {
					current.owned.delete(slug);
				}
			}
			write(current);
		},
		[],
	);

	return {
		owned: state.owned,
		excluded: state.excluded,
		has,
		isExcluded,
		toggle,
		set,
		setMany,
		setExcluded,
		setExcludedMany,
		setEffectiveOwnership,
	};
}
